import { exploredBytes, type ExploredSet } from "../vision/exploration.ts";
import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `explored` frame (ADR-100 decision 22, PROTOCOL.md 5.19): server to client, once a join, the fog cells of its map
 * the player's own character has explored. After the header: a `u16` count of columns, a `u16` count of rows, then the
 * set's cells, one bit a cell, row-major. The decoder fills the page's own set for its map, allocates nothing, never
 * throws and never reads past the frame (ADR-030).
 */

/**
 * Bytes before the first cell: the frame header and the two counts.
 */
export const EXPLORED_HEADER_BYTES = HEADER_BYTES + 4;

/**
 * Bytes of a frame for a set of these counts.
 *
 * @param columns Fog cells across.
 * @param rows Fog cells down.
 * @returns `7 + ceil(columns * rows / 8)`: 135 for 32 by 32.
 */
export function exploredFrameBytes(columns: number, rows: number): number {
  return EXPLORED_HEADER_BYTES + exploredBytes(columns, rows);
}

/**
 * Encodes a set into a caller-owned buffer.
 *
 * @param buffer Destination, at least `exploredFrameBytes(set.columns, set.rows)` long.
 * @param set The character's set.
 * @returns Bytes written.
 * @throws Error when a count does not fit a `u16`, the set's cells disagree with its counts, or the buffer is too small.
 */
export function encodeExplored(buffer: Uint8Array, set: ExploredSet): number {
  assertField(set.columns, MAX_U16, `explored columns ${set.columns}`);
  assertField(set.rows, MAX_U16, `explored rows ${set.rows}`);
  const bytes = exploredBytes(set.columns, set.rows);
  if (set.cells.length !== bytes) {
    throw new Error(`an explored set of ${set.columns} by ${set.rows} holds ${bytes} bytes, not ${set.cells.length}`);
  }
  const length = EXPLORED_HEADER_BYTES + bytes;
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte explored`);
  }
  putHeader(buffer, MessageType.Explored);
  putU16(buffer, HEADER_BYTES, set.columns);
  putU16(buffer, HEADER_BYTES + 2, set.rows);
  buffer.set(set.cells, EXPLORED_HEADER_BYTES);
  return length;
}

/**
 * Decodes an `explored` frame into the page's set for its map, replacing what it held. Never throws and reads no byte
 * past the frame: a frame shorter than its counts, a length other than `7 + ceil(columns * rows / 8)`, counts other
 * than the set's, or another type is malformed, and another version a mismatch, each leaving the set untouched. The
 * length is compared before a cell is read, so hostile counts size nothing. Bits past the last cell are cleared.
 *
 * @param frame The whole frame.
 * @param out The set for the map the page stands on.
 * @returns The verdict.
 */
export function decodeExplored(frame: Uint8Array, out: ExploredSet): DecodeVerdict {
  if (frame.length < EXPLORED_HEADER_BYTES) {
    return MALFORMED;
  }
  const columns = readU16(frame, HEADER_BYTES);
  const rows = readU16(frame, HEADER_BYTES + 2);
  if (columns !== out.columns || rows !== out.rows) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.Explored, exploredFrameBytes(columns, rows));
  if (header.kind !== "ok") {
    return header;
  }
  const bytes = out.cells.length;
  for (let index = 0; index < bytes; index += 1) {
    out.cells[index] = frame[EXPLORED_HEADER_BYTES + index] as number;
  }
  const spare = (columns * rows) & 7;
  if (spare !== 0) {
    out.cells[bytes - 1] = (out.cells[bytes - 1] as number) & ((1 << spare) - 1);
  }
  return OK;
}
